import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { overtimeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { format } from 'date-fns';
import { 
  Clock, 
  Plus, 
  CheckCircle, 
  XCircle,
  Calendar as CalendarIcon,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const Overtime = () => {
  const { user } = useAuth();
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    date: null,
    hours: '',
    reason: ''
  });

  useEffect(() => {
    fetchOvertimes();
  }, [user?.employee_id]);

  const fetchOvertimes = async () => {
    try {
      setLoading(true);
      const response = await overtimeAPI.getAll();
      setOvertimes(response.data);
    } catch (error) {
      console.error('Overtime fetch error:', error);
      toast.error('Failed to load overtime data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.hours) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await overtimeAPI.create({
        date: format(formData.date, 'yyyy-MM-dd'),
        hours: parseFloat(formData.hours),
        reason: formData.reason
      });
      toast.success('Overtime submitted!');
      setShowDialog(false);
      setFormData({ date: null, hours: '', reason: '' });
      fetchOvertimes();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit overtime');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const totalHours = overtimes.filter(o => o.status === 'approved').reduce((sum, o) => sum + (o.hours || 0), 0);
  const pendingHours = overtimes.filter(o => o.status === 'pending').reduce((sum, o) => sum + (o.hours || 0), 0);
  const estimatedPay = totalHours * 250; // R250 per hour overtime rate

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading overtime data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="overtime-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Approved Hours</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {totalHours}h
                </p>
              </div>
              <Clock className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending Hours</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pendingHours}h
                </p>
              </div>
              <Clock className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Estimated Pay</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  R {estimatedPay.toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">This Month</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {overtimes.length}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overtime List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Overtime Submissions</CardTitle>
              <CardDescription>Track your overtime work hours</CardDescription>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="new-overtime-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log Overtime
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {overtimes.length > 0 ? (
            <div className="space-y-4">
              {overtimes.map((overtime) => (
                <div 
                  key={overtime.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`overtime-item-${overtime.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {overtime.hours} hours
                      </p>
                      <p className="text-sm text-slate-500">
                        {overtime.date}
                      </p>
                      {overtime.reason && (
                        <p className="text-sm text-slate-400 mt-1">{overtime.reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(overtime.status)}
                    {overtime.status === 'approved' && (
                      <p className="text-sm text-emerald-600 font-medium mt-1">
                        +R {(overtime.hours * 250).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Overtime Logged</h3>
              <p className="text-slate-500 mb-4">Start tracking your overtime hours</p>
              <Button 
                onClick={() => setShowDialog(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Log Overtime
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Overtime Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Log Overtime</DialogTitle>
            <DialogDescription>Record your overtime work hours</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start font-normal"
                    data-testid="overtime-date-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {formData.date ? format(formData.date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Hours Worked</Label>
              <Input 
                type="number"
                placeholder="e.g., 2.5"
                value={formData.hours}
                onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                data-testid="overtime-hours-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea 
                placeholder="What were you working on?"
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="overtime-reason-input"
              />
            </div>

            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Rate:</strong> R250/hour • <strong>Estimated:</strong> R {((parseFloat(formData.hours) || 0) * 250).toLocaleString()}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-overtime-btn"
            >
              Submit Overtime
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Overtime;
